import { useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight, Lightning, FloppyDisk } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';
import PageSizeSelect from '@/components/PageSizeSelect';

const INPUT_CLS = "w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none";
const fmtEUR = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

// ── Paginated bottom bar (up to 5 page buttons around the current page) ──
function Pagination({ page, totalPages, total, onGo, pageSize, onPageSizeChange }) {
  if (total <= 0) return null;
  const computePage = (i) => {
    if (totalPages <= 5) return i + 1;
    if (page <= 3) return i + 1;
    if (page >= totalPages - 2) return totalPages - 4 + i;
    return page - 2 + i;
  };
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
      <div className="flex items-center gap-4 flex-wrap justify-center">
        <p className="text-xs text-[#6B7280]">Página {page} de {totalPages} ({total} registos)</p>
        <PageSizeSelect value={pageSize} onChange={onPageSizeChange} testId="search-page-size" />
      </div>
      {totalPages > 1 && (
      <div className="flex items-center gap-1">
        <button disabled={page <= 1} onClick={() => onGo(page - 1)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          <CaretLeft size={12} /> Anterior
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const p = computePage(i);
          return (
            <button key={p} onClick={() => onGo(p)}
              className={`w-8 h-8 text-xs font-medium rounded border transition-colors ${p === page ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-white'}`}>
              {p}
            </button>
          );
        })}
        <button disabled={page >= totalPages} onClick={() => onGo(page + 1)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          Seguinte <CaretRight size={12} />
        </button>
      </div>
      )}
    </div>
  );
}

// ── Bulk-evolve expanded panel (shows selected leads can evolve to same next state) ──
function BulkEvolvePanel({ selectedCount, evolveEvents, evolveResults, evolveForm, setEvolveForm, evolving, evolveMsg, onConfirm, onCancel }) {
  const hasError = evolveMsg && (evolveMsg.includes('erro') || evolveMsg.includes('diferentes'));
  return (
    <div data-testid="evolve-panel" className="px-5 py-4 bg-[#F0F7FF] border-b border-[#BFDBFE] space-y-3">
      <h4 className="text-xs font-semibold text-[#111827] uppercase tracking-wider">
        Evoluir {selectedCount} lead{selectedCount > 1 ? 's' : ''} seleccionada{selectedCount > 1 ? 's' : ''}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Novo Estado</label>
          <select data-testid="evolve-event" value={evolveForm.ID_Evento} onChange={e => setEvolveForm({ ...evolveForm, ID_Evento: e.target.value })} className={INPUT_CLS}>
            <option value="">Selecionar estado...</option>
            {evolveEvents.map(ev => <option key={ev.ID_EstadoNegocio} value={ev.ID_EstadoNegocio}>{ev.Descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Resultado</label>
          <select data-testid="evolve-result" value={evolveForm.ID_Resultado} onChange={e => setEvolveForm({ ...evolveForm, ID_Resultado: e.target.value })} className={INPUT_CLS}>
            <option value="">Selecionar resultado...</option>
            {evolveResults.map(r => <option key={r.Id} value={r.Id}>{r.Descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Observações</label>
          <input data-testid="evolve-obs" value={evolveForm.Observacoes} onChange={e => setEvolveForm({ ...evolveForm, Observacoes: e.target.value })} className={INPUT_CLS} placeholder="Observações..." />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="evolve-confirm-btn" onClick={onConfirm} disabled={evolving}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-[#0F1829] text-white text-xs font-medium rounded hover:bg-[#001f7a] disabled:opacity-50">
          <FloppyDisk size={14} /> {evolving ? 'A processar...' : 'Confirmar Evolução'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-xs font-medium border border-[#E5E7EB] text-[#374151] rounded hover:bg-white">Cancelar</button>
        {evolveMsg && <p className={`text-xs ml-2 ${hasError ? 'text-red-600' : 'text-green-700'}`}>{evolveMsg}</p>}
      </div>
    </div>
  );
}

/**
 * Results table with bulk selection + bulk-evolve panel + pagination.
 */
export default function LeadSearchResults({
  results, total, totalPages, page,
  selected, toggleSelect, toggleSelectAll,
  showEvolve, evolveEvents, evolveResults, evolveForm, setEvolveForm, evolving, evolveMsg,
  onOpenEvolve, onEvolveConfirm, onEvolveCancel, onGoPage,
  pageSize, onPageSizeChange,
}) {
  const navigate = useNavigate();
  if (results === null) return null;

  return (
    <div data-testid="search-results" className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#111827]">Resultados ({total})</h3>
        {selected.size > 0 && (
          <button data-testid="evolve-btn" onClick={onOpenEvolve}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#0F1829] text-white text-xs font-medium rounded hover:bg-[#001f7a] transition-colors">
            <Lightning size={14} /> Evoluir {selected.size} lead{selected.size > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {showEvolve && (
        <BulkEvolvePanel
          selectedCount={selected.size}
          evolveEvents={evolveEvents}
          evolveResults={evolveResults}
          evolveForm={evolveForm}
          setEvolveForm={setEvolveForm}
          evolving={evolving}
          evolveMsg={evolveMsg}
          onConfirm={onEvolveConfirm}
          onCancel={onEvolveCancel}
        />
      )}

      {results.length > 0 ? (
        <>
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="w-10 px-3 py-2.5">
                <input data-testid="select-all" type="checkbox" checked={results.length > 0 && selected.size === results.length}
                  onChange={toggleSelectAll} className="rounded border-[#D1D5DB]" />
              </th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">ID</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Consultor</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Cliente</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Empresa</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Estado Atual</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Campanha</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Início</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Últ. Estado</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Observações</th>
              <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Valor</th>
            </tr></thead>
            <tbody>
              {results.map(r => (
                <tr key={r.ID_Negocio} data-testid={`result-${r.ID_Negocio}`}
                  className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors ${selected.has(r.ID_Negocio) ? 'bg-[#0F1829]/5' : ''}`}>
                  <td className="w-10 px-3 py-2.5">
                    <input type="checkbox" checked={selected.has(r.ID_Negocio)}
                      onChange={() => toggleSelect(r.ID_Negocio)} className="rounded border-[#D1D5DB]" />
                  </td>
                  <td className="px-3 py-2.5 text-sm font-mono text-[#0F1829] underline cursor-pointer" onClick={() => navigate(`/negocios/${r.ID_Negocio}`)}>{r.ID_Negocio}</td>
                  <td className="px-3 py-2.5 text-sm"><PersonLink id={r.ID_Consultor} name={r.NomeConsultor} /></td>
                  <td className="px-3 py-2.5 text-sm"><PersonLink id={r.ID_Cliente} name={r.NomeCliente} /></td>
                  <td className="px-3 py-2.5 text-sm text-[#6B7280]">{r.NomeEmpresa || '-'}</td>
                  <td className="px-3 py-2.5"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">{r.NomeEstadoAtual}</span></td>
                  <td className="px-3 py-2.5">
                    {r.CampanhaDesc ? (
                      <span
                        data-testid={`result-campanha-${r.ID_Negocio}`}
                        title={r.CampanhaId ? `Landing Page: ${r.CampanhaId}` : ''}
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 rounded bg-[#F5F2EB] text-[#645E50] border border-[#E5E1D3] whitespace-nowrap"
                      >
                        {r.CampanhaDesc}
                      </span>
                    ) : <span className="text-[9px] text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-[#6B7280]">{r.DataInicio || '-'}</td>
                  <td className="px-3 py-2.5 text-sm text-[#6B7280]" data-testid={`result-ultimo-estado-${r.ID_Negocio}`}>{r.DataUltimoEstado || '-'}</td>
                  <td
                    className="px-3 py-2.5 text-xs text-[#6B7280] max-w-[260px]"
                    title={r.Observacao || ''}
                    data-testid={`result-obs-${r.ID_Negocio}`}
                  >
                    <span
                      className="overflow-hidden"
                      style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}
                    >
                      {r.Observacao || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-sm font-mono text-right">{fmtEUR(r.ValorPrevisto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={page} totalPages={totalPages} total={total} onGo={onGoPage}
            pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
        </>
      ) : (
        <p className="text-center py-6 text-sm text-[#6B7280]">Sem resultados para a pesquisa</p>
      )}
    </div>
  );
}
